/**
 * Shared utility functions for community events
 */

/**
 * Convert time string (HH:MM or h:mm AM/PM) to 24-hour format
 * @param {string} timeStr - Time string in either format
 * @returns {string} Time in HH:MM format
 */
export function parseTimeTo24Hour(timeStr) {
  if (!timeStr) return "00:00";

  // Already in 24-hour format (HH:MM)
  if (/^\d{2}:\d{2}$/.test(timeStr)) {
    return timeStr;
  }

  // Parse 12-hour format (e.g., "01:00 PM")
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (match) {
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3].toUpperCase();

    if (period === "PM" && hours !== 12) hours += 12;
    if (period === "AM" && hours === 12) hours = 0;

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  }

  return timeStr;
}

/**
 * Format time for display (24-hour to 12-hour)
 * @param {string} timeStr - Time string
 * @returns {string} Time in 12-hour format with AM/PM
 */
export function formatTimeForDisplay(timeStr) {
  if (!timeStr) return "";

  // If already in 12-hour format, return as is
  if (/AM|PM/i.test(timeStr)) return timeStr;

  // Convert 24-hour to 12-hour
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;

  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
}

/**
 * Get event start datetime as a Date object
 * @param {string} nextEventDate - ISO date string
 * @param {string} startTime - Time string
 * @returns {Date|null} Date object or null
 */
export function getEventStartDateTime(nextEventDate, startTime) {
  if (!nextEventDate) return null;
  const eventDate = new Date(nextEventDate);
  const time24 = parseTimeTo24Hour(startTime);
  const [hours, minutes] = time24.split(":").map(Number);
  eventDate.setHours(hours, minutes, 0, 0);
  return eventDate;
}

/**
 * Calculate countdown to event
 * @param {string} nextEventDate - ISO date string
 * @param {string} startTime - Time string
 * @returns {Object|null} Countdown object with days, hours, minutes, isLive
 */
export function calculateCountdown(nextEventDate, startTime) {
  const eventDateTime = getEventStartDateTime(nextEventDate, startTime);
  if (!eventDateTime) return null;

  const now = new Date();
  const diff = eventDateTime.getTime() - now.getTime();

  if (diff <= 0) {
    return { isLive: true, days: 0, hours: 0, minutes: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  return { isLive: false, days, hours, minutes };
}

/**
 * Generate Google Calendar URL for an event
 * @param {Object} event - Event object with title, description, nextEventDate, startTime, endTime, meetingLink
 * @returns {string|null} Google Calendar URL or null
 */
export function generateGoogleCalendarUrl(event) {
  const { title, description, nextEventDate, startTime, endTime, meetingLink } = event;

  if (!nextEventDate) return null;

  // Parse the date
  const eventDate = new Date(nextEventDate);
  const year = eventDate.getFullYear();
  const month = (eventDate.getMonth() + 1).toString().padStart(2, "0");
  const day = eventDate.getDate().toString().padStart(2, "0");

  // Parse times to 24-hour format
  const start24 = parseTimeTo24Hour(startTime);
  const end24 = parseTimeTo24Hour(endTime);

  // Format for Google Calendar: YYYYMMDDTHHmmss
  const startDateTime = `${year}${month}${day}T${start24.replace(":", "")}00`;
  const endDateTime = `${year}${month}${day}T${end24.replace(":", "")}00`;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Community Event",
    dates: `${startDateTime}/${endDateTime}`,
    details: description || "",
    location: meetingLink || "",
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Generate Google Calendar URL for a session (recurring event instance)
 * @param {Object} session - Session/instance object with instanceDate, startTime, endTime, title, description, meetingLink
 * @param {string} parentTitle - Fallback title from parent event
 * @returns {string|null} Google Calendar URL or null
 */
export function generateSessionCalendarUrl(session, parentTitle) {
  const dateField = session.instanceDate || session.nextEventDate;
  if (!dateField) return null;

  const eventDate = new Date(dateField);
  const year = eventDate.getFullYear();
  const month = (eventDate.getMonth() + 1).toString().padStart(2, "0");
  const day = eventDate.getDate().toString().padStart(2, "0");

  const start24 = parseTimeTo24Hour(session.startTime);
  const end24 = parseTimeTo24Hour(session.endTime);

  const startDateTime = `${year}${month}${day}T${start24.replace(":", "")}00`;
  const endDateTime = `${year}${month}${day}T${end24.replace(":", "")}00`;

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: session.title || parentTitle || "Community Event",
    dates: `${startDateTime}/${endDateTime}`,
    details: session.description || "",
    location: session.meetingLink || "",
  });

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Format date and time for display
 * @param {string} dateString - ISO date string
 * @param {string} startTime - Start time string
 * @param {string} endTime - End time string
 * @returns {string} Formatted date/time string
 */
export function formatEventDateTime(dateString, startTime, endTime) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  const monthDay = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const displayStart = formatTimeForDisplay(startTime);
  const displayEnd = formatTimeForDisplay(endTime);
  return `${monthDay}, ${displayStart || ""} - ${displayEnd || ""}`;
}

/**
 * Format date for short display (e.g., "Jan 15")
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export function formatShortDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Format full date with day of week
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date with weekday
 */
export function formatFullDate(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
